import React, { useState, useEffect, useCallback } from 'react';
import {
  getVatStatementReport,
  getProfitLossReport,
  getFinancialSummary
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TrendingUp, Receipt, BarChart3, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Sub-components extracted for maintainability
import { SummaryTab } from './financial/SummaryTab';
import { VatTab } from './financial/VatTab';
import { ProfitLossTab } from './financial/ProfitLossTab';

export default function FinancialReportsPage() {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [periodType, setPeriodType] = useState('quarterly');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Report data states
  const [summaryData, setSummaryData] = useState(null);
  const [vatData, setVatData] = useState(null);
  const [plData, setPlData] = useState(null);

  const loadReport = useCallback(async (reportType) => {
    setLoading(true);
    try {
      switch (reportType) {
        case 'summary': {
          const sum = await getFinancialSummary(year);
          setSummaryData(sum.data);
          break;
        }
        case 'vat': {
          const vat = await getVatStatementReport({
            start_date: dateRange.start,
            end_date: dateRange.end,
            period_type: periodType
          });
          setVatData(vat.data);
          break;
        }
        case 'pl': {
          const pl = await getProfitLossReport({
            start_date: dateRange.start,
            end_date: dateRange.end
          });
          setPlData(pl.data);
          break;
        }
        default:
          break;
      }
    } catch (error) {
      toast.error('Failed to load report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [year, dateRange, periodType]);

  useEffect(() => {
    loadReport(activeTab);
  }, [activeTab, year, loadReport]);

  const handleRefresh = useCallback(() => {
    loadReport(activeTab);
  }, [activeTab, loadReport]);

  const exportToCsv = useCallback((data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, []);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="financial-reports-title">Financial Reports</h1>
          <p className="text-muted-foreground">VAT Returns, P&L, and Financial Analysis</p>
        </div>
        <div className="flex items-center gap-4">
          {activeTab === 'summary' ? (
            <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-[120px]" data-testid="year-selector">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Label className="text-sm">From:</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))}
                  className="w-36"
                  data-testid="date-from"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">To:</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))}
                  className="w-36"
                  data-testid="date-to"
                />
              </div>
            </>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="summary" className="flex items-center gap-1" data-testid="tab-summary">
            <BarChart3 className="w-4 h-4" /> Summary
          </TabsTrigger>
          <TabsTrigger value="vat" className="flex items-center gap-1" data-testid="tab-vat">
            <Receipt className="w-4 h-4" /> VAT Statement
          </TabsTrigger>
          <TabsTrigger value="pl" className="flex items-center gap-1" data-testid="tab-pl">
            <TrendingUp className="w-4 h-4" /> P&L Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-6">
          <SummaryTab loading={loading} summaryData={summaryData} year={year} />
        </TabsContent>

        <TabsContent value="vat" className="mt-6">
          <VatTab
            loading={loading}
            vatData={vatData}
            periodType={periodType}
            setPeriodType={setPeriodType}
            onRefresh={handleRefresh}
            exportToCsv={exportToCsv}
            handlePrint={handlePrint}
          />
        </TabsContent>

        <TabsContent value="pl" className="mt-6">
          <ProfitLossTab loading={loading} plData={plData} exportToCsv={exportToCsv} handlePrint={handlePrint} />
        </TabsContent>
      </Tabs>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #pl-report, #pl-report * {
            visibility: visible;
          }
          #pl-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
